import React, { useState } from 'react'
import DashboardLayout from '../../../layoutcontainers/DashboardLayout'
import MaterialBox from '../../../components/MaterialBox'
import MaterialTypography from '../../../components/MaterialTypography'
import { Grid, Stack, Divider } from '@mui/material'
import MaterialButton from '../../../components/MaterialButton'

// Modular Step Components
import ProjectDetails from './components/ProjectDetails'
import Introduction from './components/Introduction'
import ScopeOfTesting from './components/ScopeOfTesting'
import TestEnvironment from './components/TestEnvironment'
import TestCoverageSummary from './components/TestCoverageSummary'
import DefectSummary from './components/DefectSummary'
import RisksAndMitigation from './components/RisksAndMitigation'
import ExitCriteriaStatus from './components/ExitCriteriaStatus'
import FinalRecommendation from './components/FinalRecommendation'
import SignOffApprovals from './components/SignOffApprovals'
import VerticalStepper from './components/VerticalStepper'

const Reports = () => {
  const [activeStep, setActiveStep] = useState(0)
  const steps = [
    'Project Details',
    'Introduction',
    'Scope of Testing',
    'Test Environment Details',
    'Test Coverage Summary',
    'Defect Summary',
    'Risks & Mitigation',
    'Exit Criteria Status',
    'Final QA Recommendation',
    'Sign-Off & Approvals'
  ]

  const [formData, setFormData] = useState({
    projectName: '',
    applicationType: '',
    modulesCovered: '',
    releaseDocVersion: '',
    applicationVersion: '',
    preparedBy: '',
    testCycleDate: '',
    introduction: '',
    scopeText: '',
    scopePoints: [],
    webEnvironment: '',
    mobileApp: '',
    browsersTested: '',
    database: '',
    server: '',
    testDevice: '',
    webCoverage: [
      { testType: 'Functional Testing', total: 100, passed: 95, failed: 5, remarks: '' },
      { testType: 'UI Testing', total: 50, passed: 48, failed: 2, remarks: '' },
      { testType: 'Regression Testing', total: 30, passed: 30, failed: 0, remarks: '' }
    ],
    mobileCoverage: [
      { testType: 'Functional Testing', total: 80, passed: 75, failed: 5, remarks: '' },
      { testType: 'Compatibility Testing', total: 40, passed: 38, failed: 2, remarks: '' }
    ],
    totalDefects: '0',
    reopenedDefects: '0',
    repetitiveDefects: '0',
    defects: [{ severity: '', count: '', status: '' }],
    risks: [{ risk: '', impact: '', mitigation: '' }],
    exitCriteria: {
      allPlannedExecuted: '',
      allCriticalHighClosed: '',
      regressionCompleted: '',
      stabilityVerified: '',
      summaryPrepared: '',
      stakeholderReviewCompleted: ''
    },
    finalRecommendation: '',
    signOff: {
      qaLead: { name: '', signature: '', date: '' },
      devManager: { name: '', signature: '', date: '' },
      projectManager: { name: '', signature: '', date: '' },
      stakeholder: { name: '', signature: '', date: '' }
    }
  })

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleDateChange = (date) => {
    setFormData(prev => ({ ...prev, testCycleDate: date[0] }))
  }

  const handleAddScopePoint = () => {
    if (formData.scopeText.trim()) {
      setFormData(prev => ({
        ...prev,
        scopePoints: [...prev.scopePoints, prev.scopeText],
        scopeText: ''
      }))
    }
  }

  const handleTableRemarkChange = (type, index, value) => {
    setFormData(prev => {
      const updatedTable = [...prev[type]];
      updatedTable[index].remarks = value;
      return { ...prev, [type]: updatedTable };
    });
  }

  // Defect Row Handlers
  const handleDefectRowChange = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.defects];
      updated[index][field] = value;
      return { ...prev, defects: updated };
    });
  }
  const addDefectRow = () => {
    setFormData(prev => ({
      ...prev,
      defects: [...prev.defects, { severity: '', count: '', status: '' }]
    }));
  }
  const removeDefectRow = (index) => {
    setFormData(prev => ({
      ...prev,
      defects: prev.defects.filter((_, i) => i !== index)
    }));
  }

  // Risk Row Handlers
  const handleRiskRowChange = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.risks];
      updated[index][field] = value;
      return { ...prev, risks: updated };
    });
  }
  const addRiskRow = () => {
    setFormData(prev => ({
      ...prev,
      risks: [...prev.risks, { risk: '', impact: '', mitigation: '' }]
    }));
  }
  const removeRiskRow = (index) => {
    setFormData(prev => ({
      ...prev,
      risks: prev.risks.filter((_, i) => i !== index)
    }));
  }

  // Exit Criteria Handler
  const handleExitCriteriaChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      exitCriteria: { ...prev.exitCriteria, [key]: value }
    }));
  }

  // Sign Off Handler
  const handleSignOffChange = (role, field, value) => {
    setFormData(prev => ({
      ...prev,
      signOff: {
        ...prev.signOff,
        [role]: { ...prev.signOff[role], [field]: value }
      }
    }));
  }

  const renderStepContent = (step) => {
    switch (step) {
      case 0: return <ProjectDetails formData={formData} handleChange={handleChange} handleDateChange={handleDateChange} />
      case 1: return <Introduction formData={formData} handleChange={handleChange} />
      case 2: return <ScopeOfTesting formData={formData} handleChange={handleChange} handleAddScopePoint={handleAddScopePoint} />
      case 3: return <TestEnvironment formData={formData} handleChange={handleChange} />
      case 4: return <TestCoverageSummary formData={formData} handleTableRemarkChange={handleTableRemarkChange} />
      case 5: return <DefectSummary formData={formData} handleChange={handleChange} handleDefectRowChange={handleDefectRowChange} addDefectRow={addDefectRow} removeDefectRow={removeDefectRow} />
      case 6: return <RisksAndMitigation formData={formData} handleRiskRowChange={handleRiskRowChange} addRiskRow={addRiskRow} removeRiskRow={removeRiskRow} />
      case 7: return <ExitCriteriaStatus formData={formData} handleExitCriteriaChange={handleExitCriteriaChange} />
      case 8: return <FinalRecommendation formData={formData} handleChange={handleChange} />
      case 9: return <SignOffApprovals formData={formData} handleSignOffChange={handleSignOffChange} />
      default: return null
    }
  }

  return (
    <DashboardLayout>
      <MaterialBox py={1} px={1}>
        <Stack mb={2}>
          <MaterialTypography variant="h5" fontWeight="medium">
            Report Generation
          </MaterialTypography>
          <MaterialTypography variant="caption" color="text">
            Fill in the details to generate the report.
          </MaterialTypography>
        </Stack>

        <Grid container spacing={1}>
          {/* Vertical Stepper Sidebar */}
          <Grid size={{ xs: 12, md: 3 }}>
            <MaterialBox
              bgColor="white"
              borderRadius="lg"
              shadow="lg"
              p={2}
              height="100%"
            >
              <VerticalStepper
                steps={steps}
                activeStep={activeStep}
                setActiveStep={setActiveStep}
              />
            </MaterialBox>
          </Grid>

          {/* Step Content Area */}
          <Grid size={{ xs: 12, md: 9 }}>
            <MaterialBox
              bgColor="white"
              borderRadius="lg"
              shadow="lg"
              p={2}
              minHeight="450px"
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
            >
              <MaterialBox mb={3}>
                <MaterialTypography variant="h6" fontWeight="medium" mb={2}>
                  {steps[activeStep]}
                </MaterialTypography>
                <Divider />
                <MaterialBox mt={3}>
                  <MaterialBox
                    key={activeStep}
                    sx={{
                      animation: 'fadeInUp 0.5s ease-out',
                      '@keyframes fadeInUp': {
                        '0%': { opacity: 0, transform: 'translateY(20px)' },
                        '100%': { opacity: 1, transform: 'translateY(0)' }
                      }
                    }}
                  >
                    {renderStepContent(activeStep)}
                  </MaterialBox>
                </MaterialBox>
              </MaterialBox>

              <Stack direction="row" justifyContent="space-between" mt={4}>
                <MaterialButton
                  size='small'
                  variant="outlined"
                  color="dark"
                  disabled={activeStep === 0}
                  onClick={() => setActiveStep(prev => prev - 1)}
                >
                  Back
                </MaterialButton>
                <MaterialButton
                  size='small'
                  variant="contained"
                  color="dark"
                  onClick={() => activeStep === steps.length - 1 ? console.log('Submit', formData) : setActiveStep(prev => prev + 1)}
                >
                  {activeStep === steps.length - 1 ? 'Generate Report' : 'Next'}
                </MaterialButton>
              </Stack>
            </MaterialBox>
          </Grid>
        </Grid>
      </MaterialBox>
    </DashboardLayout>
  )
}

export default Reports


